import {useEffect} from 'react';
import {useLocation} from 'react-router-dom';
import {adminSection} from './adminSections';
import {PLATFORM_CODENAME} from './platform';

// The app, as opposed to the customer deployment (PLATFORM_CODENAME). A constant on purpose: the
// deploy sets VITE_PRODUCT_NAME to a customer name ("Pioneer"), which is not this.
const APP_NAME = 'PRI';

// One title per top-level route, shared by the page header (which the design system makes
// mandatory on every screen) and the browser tab, so the two can never disagree.
export const PAGE_TITLES: Record<string, string> = {
  dashboard: 'Dashboard',
  submission: 'Submission',
  investigations: 'Investigations',
  content: 'Content',
  submissions: 'Submissions',
  jobs: 'Jobs',
  queues: 'Queue',
  'p0-alerts': 'P0 Alerts',
  'weekly-qa': 'Weekly QA',
  'qc-review': 'QC Review',
  'rejected-submissions': 'Rejected Submissions',
  'submissions-dashboard': 'Submissions dashboard',
  reporting: 'Reporting',
  collections: 'Collections',
  'whats-new': "What's new",
  admin: 'Admin',
  login: 'Sign in'
};

// Admin pages with their own route rather than an ADMIN_SECTIONS entry, titled like the sections.
const STANDALONE_ADMIN_TITLES: Record<string, string> = {workflows: 'Risk guidance'};

// What the in-shell 404 (App.tsx's catch-all) is called in the header and the tab.
export const NOT_FOUND_TITLE = 'Page not found';

// Keyed on the first segment only, so a bad path under a real section (`/jobs/42/nope`) keeps that
// section's title even though the 404 renders. A segment no route owns is the 404's. `/` has no
// title of its own: it only ever redirects. The exception is /admin/<section>: each settings
// section is its own page, so it is titled by its section name rather than "Admin".
export function pageTitle(pathname: string): string | undefined {
  const [segment, subSegment] = pathname.split('/').filter(Boolean);
  if (segment === undefined) {
    return undefined;
  }

  if (segment === 'admin' && subSegment !== undefined) {
    return (
      adminSection(subSegment)?.label ??
      (Object.hasOwn(STANDALONE_ADMIN_TITLES, subSegment)
        ? STANDALONE_ADMIN_TITLES[subSegment]
        : NOT_FOUND_TITLE)
    );
  }

  return Object.hasOwn(PAGE_TITLES, segment) ? PAGE_TITLES[segment] : NOT_FOUND_TITLE;
}

// `PIONEER | Dashboard | PRI`: the customer codename first so several deployments' tabs tell apart,
// the page next, the app last. `/` has no page name, so it drops the middle segment.
export function documentTitle(pathname: string): string {
  return [PLATFORM_CODENAME, pageTitle(pathname), APP_NAME].filter(Boolean).join(' | ');
}

export function useDocumentTitle() {
  const {pathname} = useLocation();
  useEffect(() => {
    document.title = documentTitle(pathname);
  }, [pathname]);
}
